/**
 * /admin/blogs — list all blog posts for writers (editor) and admins.
 * RLS: editor/admin JWT claim `user_role` (see supabase blog_posts policies).
 * Editors only see their own posts; admins see all (client-side filter on created_by when role is editor).
 */

import { AnimatePresence, motion } from 'framer-motion';
import {
  Check,
  Copy,
  Key,
  Pencil,
  Plus,
  Search,
  Trash2,
  FileText,
  X,
} from 'lucide-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { Session } from '@supabase/supabase-js';

import { RefreshCw } from 'lucide-react';

import { useAuthentication } from '../../hooks/useAuthentication';
import { supabase } from '../../lib/supabase';
import type { BlogPost } from '../../types/blog';

const PAGE_SIZE = 20;
type StatusFilter = 'all' | 'draft' | 'scheduled' | 'published' | 'archived';
const STATUS_OPTIONS: { id: StatusFilter; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'draft', label: 'Draft' },
  { id: 'scheduled', label: 'Scheduled' },
  { id: 'published', label: 'Published' },
  { id: 'archived', label: 'Archived' },
];

function getJwtUserRole(session: Session | null): 'admin' | 'editor' | null {
  if (!session) return null;
  const u = session.user;
  const am = u.app_metadata as Record<string, unknown> | undefined;
  const um = u.user_metadata as Record<string, unknown> | undefined;
  const a = (am?.user_role as string) || (um?.user_role as string);
  if (a === 'admin' || a === 'editor') return a;
  try {
    const part = session.access_token.split('.')[1];
    const b64 = part.replace(/-/g, '+').replace(/_/g, '/');
    const p = JSON.parse(atob(b64)) as { user_role?: string };
    if (p.user_role === 'admin' || p.user_role === 'editor') return p.user_role;
  } catch {
    /* ignore */
  }
  return null;
}

function escapeIlike(s: string): string {
  return s.replace(/\\/g, '\\\\').replace(/%/g, '\\%').replace(/_/g, '\\_');
}

const BlogListInner: React.FC<{ embedded?: boolean }> = ({ embedded }) => {
  const { session, loading: authLoading } = useAuthentication();
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState<BlogPost[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<{ id: string; title: string } | null>(null);
  const [draftAwaitingCount, setDraftAwaitingCount] = useState(0);

  // Rotate API key (admin only)
  type RotateKeyState = 'idle' | 'confirm' | 'generating' | 'done' | 'error';
  const [rotateState, setRotateState] = useState<RotateKeyState>('idle');
  const [rotateKey, setRotateKey] = useState<string>('');        // shown once, cleared on close
  const [rotatePrefix, setRotatePrefix] = useState<string>('');  // RPC return value (8 chars)
  const [rotateError, setRotateError] = useState<string>('');
  const [keyCopied, setKeyCopied] = useState(false);
  const keyRef = useRef<HTMLInputElement>(null);

  const role = getJwtUserRole(session);
  const userId = session?.user?.id ?? null;

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchInput), 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, statusFilter]);

  const load = useCallback(async () => {
    if (authLoading) return;
    if (!session) {
      setLoading(false);
      setError('Sign in with your writer account to manage blog posts.');
      setRows([]);
      setTotal(0);
      return;
    }
    if (!role) {
      setLoading(false);
      setError('Your account does not have editor or admin access. Ask an administrator to set user_role in your JWT (user_role: editor or admin).');
      setRows([]);
      setTotal(0);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const from = (page - 1) * PAGE_SIZE;
      const to = from + PAGE_SIZE - 1;
      const q = escapeIlike(debouncedSearch.trim());

      let query = supabase
        .from('blog_posts')
        .select('*', { count: 'exact' })
        .order('updated_at', { ascending: false });

      if (statusFilter !== 'all') {
        query = query.eq('status', statusFilter);
      }

      if (q.length > 0) {
        query = query.or(`title.ilike.%${q}%,slug.ilike.%${q}%`);
      }

      const { data, error: err, count } = await query.range(from, to);

      if (err) throw err;
      setRows((data ?? []) as BlogPost[]);
      setTotal(count ?? 0);

      if (role === 'admin') {
        const { count: draftCount } = await supabase
          .from('blog_posts')
          .select('id', { count: 'exact', head: true })
          .eq('status', 'draft');
        setDraftAwaitingCount(draftCount ?? 0);
      } else {
        setDraftAwaitingCount(0);
      }
    } catch (e) {
      console.error('[BlogListPage]', e);
      setError(e instanceof Error ? e.message : 'Failed to load posts');
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [session, authLoading, role, userId, page, statusFilter, debouncedSearch]);

  useEffect(() => {
    void load();
  }, [load]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const showToast = (msg: string) => {
    setToast(msg);
    window.setTimeout(() => setToast(null), 4000);
  };

  const handleDeleteConfirmed = async () => {
    if (!confirmDelete) return;
    const { id } = confirmDelete;
    setConfirmDelete(null);
    setDeletingId(id);
    try {
      const { error: delErr } = await supabase.from('blog_posts').delete().eq('id', id);
      if (delErr) throw delErr;
      showToast('Post deleted');
      void load();
    } catch (e) {
      console.error(e);
      setError(e instanceof Error ? e.message : 'Delete failed');
    } finally {
      setDeletingId(null);
    }
  };

  const navigateTo = (path: string) => {
    window.history.pushState({}, '', path);
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  /** Generates a 48-char URL-safe random key entirely in the browser — never touches a server. */
  const generateApiKey = (): string => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';
    const bytes = new Uint8Array(48);
    crypto.getRandomValues(bytes);
    return Array.from(bytes, (b) => chars[b % chars.length]).join('');
  };

  const handleRotateKey = async () => {
    const newKey = generateApiKey();
    setRotateKey(newKey);
    setRotateState('generating');
    setRotateError('');
    try {
      const { data, error: rpcErr } = await supabase.rpc(
        'admin_rotate_blog_publish_key',
        { new_key: newKey },
      );
      if (rpcErr) throw rpcErr;
      setRotatePrefix((data as string) ?? newKey.slice(0, 8));
      setRotateState('done');
    } catch (e) {
      setRotateKey('');     // wipe key on failure — do not expose it in error state
      setRotateError(e instanceof Error ? e.message : 'Rotation failed');
      setRotateState('error');
    }
  };

  const handleCopyKey = () => {
    if (!rotateKey) return;
    navigator.clipboard.writeText(rotateKey).then(() => {
      setKeyCopied(true);
      setTimeout(() => setKeyCopied(false), 2500);
    });
  };

  const handleCloseRotateModal = () => {
    setRotateState('idle');
    setRotateKey('');    // wipe from memory
    setRotatePrefix('');
    setRotateError('');
    setKeyCopied(false);
  };

  return (
    <>
    <div className={embedded ? 'h-full ds-page' : 'min-h-screen ds-page'}>

      {/* Toast */}
      {toast && (
        <div
          className="fixed bottom-6 right-6 z-50 ds-recessed-ok t-meta"
          role="status"
        >
          {toast}
        </div>
      )}

      <div className={`${embedded ? '' : 'max-w-7xl mx-auto'} px-4 sm:px-6 py-6`}>
        {/* Top bar — only shown when NOT embedded (Layout provides header when embedded) */}
        {!embedded && (
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
            <div>
              <div className="flex items-center gap-2 t-meta mb-1">
                <span className="t-strong font-semibold">Blogs</span>
              </div>
              <h1 className="t-page-title flex items-center gap-2">
                <FileText className="w-7 h-7" style={{ color: 'var(--ds-brand)' }} />
                Blog posts
              </h1>
              {role && (
                <p className="t-meta mt-1 capitalize">
                  Signed in as {role}
                  {role === 'editor' ? ' (your posts only)' : ' (all posts)'}
                </p>
              )}
            </div>
            <div className="flex items-center gap-2 w-full sm:w-auto">
              {role === 'admin' && (
                <button
                  type="button"
                  onClick={() => setRotateState('confirm')}
                  title="Rotate API key"
                  className="ds-btn ds-btn-secondary"
                >
                  <Key className="w-4 h-4" />
                  Rotate API key
                </button>
              )}
              <button
                type="button"
                onClick={() => navigateTo('/admin/blogs/new')}
                className="ds-btn ds-btn-primary w-full sm:w-auto"
              >
                <Plus className="w-4 h-4" />
                New post
              </button>
            </div>
          </div>
        )}

        {role === 'admin' && draftAwaitingCount > 0 && statusFilter !== 'draft' && (
          <div
            className="mb-4 rounded-lg border px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
            style={{
              borderColor: 'var(--ds-warn)',
              background: 'color-mix(in srgb, var(--ds-warn) 12%, transparent)',
            }}
            role="status"
          >
            <p className="t-meta m-0">
              <strong>{draftAwaitingCount}</strong> blog draft{draftAwaitingCount === 1 ? '' : 's'} awaiting review
              {' '}— expand content, QA, then publish. Admins also get bell notifications when drafts are created.
            </p>
            <button
              type="button"
              className="ds-btn ds-btn-secondary shrink-0"
              onClick={() => setStatusFilter('draft')}
            >
              Show drafts
            </button>
          </div>
        )}

        {/* When embedded the top action bar */}
        {embedded && (
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
            <div>
              <h2 className="t-h3 flex items-center gap-2">
                <FileText className="w-5 h-5" style={{ color: 'var(--ds-brand)' }} />
                Blog posts
              </h2>
              {role && (
                <p className="t-meta mt-0.5 capitalize">
                  {role === 'editor' ? 'Showing all posts you can manage' : 'Showing all posts (admin)'}
                </p>
              )}
            </div>
            <div className="flex items-center gap-2 w-full sm:w-auto">
              {role === 'admin' && (
                <button
                  type="button"
                  onClick={() => setRotateState('confirm')}
                  title="Rotate API key"
                  className="ds-btn ds-btn-secondary"
                >
                  <Key className="w-4 h-4" />
                  Rotate API key
                </button>
              )}
              <button
                type="button"
                onClick={() => navigateTo('/admin/blogs/new')}
                className="ds-btn ds-btn-primary w-full sm:w-auto"
              >
                <Plus className="w-4 h-4" />
                New post
              </button>
            </div>
          </div>
        )}

        {/* Search + filter chips */}
        <div className="space-y-3 mb-5">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'var(--ds-text-muted)' }} />
            <input
              type="search"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search by title or slug…"
              className="ds-input pl-10"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            {STATUS_OPTIONS.map((opt) => (
              <button
                key={opt.id}
                type="button"
                onClick={() => setStatusFilter(opt.id)}
                className="ds-chip ds-chip-button min-h-[44px] touch-manipulation"
                aria-selected={statusFilter === opt.id ? 'true' : undefined}
                style={
                  statusFilter === opt.id
                    ? undefined
                    : undefined
                }
                data-inactive={statusFilter !== opt.id ? '' : undefined}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {error && !loading && (
          <div className="mb-4 ds-recessed-warn t-meta">
            {error}
          </div>
        )}

        {/* Table */}
        <div className="ds-card-flush">
          {loading && (
            <div className="py-20 flex justify-center">
              <div className="w-8 h-8 rounded-full border-2 border-t-transparent animate-spin"
                   style={{ borderColor: 'var(--ds-brand)', borderTopColor: 'transparent' }} />
            </div>
          )}
          {!loading && rows.length === 0 && !error && (
            <div className="py-16 text-center">
              <p className="t-h3">No posts found</p>
              <p className="t-meta mt-1">Try a different search or status filter.</p>
            </div>
          )}
          {!loading && rows.length > 0 && (
            <div className="overflow-x-auto">
              <table className="ds-table">
                <thead>
                  <tr>
                    <th className="px-4 py-3">Title</th>
                    <th className="px-4 py-3">Slug</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">Author</th>
                    <th className="px-4 py-3">Updated</th>
                    <th className="px-4 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id}>
                      <td className="font-medium max-w-[200px] truncate" title={r.title}>
                        {r.title}
                      </td>
                      <td className="t-mono t-tag max-w-[160px] truncate" title={r.slug}
                          style={{ color: 'var(--ds-brand)' }}>
                        {r.slug}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`ds-chip capitalize ${
                            r.status === 'published'
                              ? 'ds-chip-ok'
                              : r.status === 'draft'
                                ? ''
                                : r.status === 'scheduled'
                                  ? 'ds-chip-info'
                                  : ''
                          }`}
                        >
                          {r.status}
                        </span>
                      </td>
                      <td className="t-meta max-w-[120px] truncate" title={r.author_name ?? ''}>
                        {r.author_name ?? '—'}
                      </td>
                      <td className="t-meta whitespace-nowrap">
                        {r.updated_at
                          ? new Date(r.updated_at).toLocaleString(undefined, {
                              dateStyle: 'medium',
                              timeStyle: 'short',
                            })
                          : '—'}
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        <button
                          type="button"
                          onClick={() => navigateTo(`/admin/blogs/${r.id}/edit`)}
                          className="ds-btn ds-btn-ghost ds-btn-sm mr-3"
                          style={{ color: 'var(--ds-brand)' }}
                        >
                          <Pencil className="w-3.5 h-3.5" />
                          Edit
                        </button>
                        <button
                          type="button"
                          disabled={deletingId === r.id}
                          onClick={() => setConfirmDelete({ id: r.id, title: r.title })}
                          className="ds-btn ds-btn-ghost ds-btn-sm hover:text-[var(--ds-danger)] disabled:opacity-50"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {!loading && total > PAGE_SIZE && (
          <div className="mt-6 flex items-center justify-between t-meta">
            <span>
              Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} of {total}
            </span>
            <div className="flex gap-2">
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="ds-btn ds-btn-secondary ds-btn-sm disabled:opacity-40"
              >
                Previous
              </button>
              <button
                type="button"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
                className="ds-btn ds-btn-secondary ds-btn-sm disabled:opacity-40"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>

    {/* Delete confirmation dialog — portal so CSS transforms can't trap it */}
    {createPortal(
      <AnimatePresence>
        {confirmDelete && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm"
            onClick={() => setConfirmDelete(null)}
            style={{ pointerEvents: 'all' }}
          >
            <motion.div
              initial={{ scale: 0.96, opacity: 0, y: 6 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.96, opacity: 0, y: 6 }}
              transition={{ type: 'spring', stiffness: 320, damping: 28 }}
              className="ds-card-pop w-full max-w-sm mx-4 overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Title bar — mirrors LogoutDialog */}
              <div
                className="flex items-center justify-between px-3 py-2 shrink-0 select-none"
                style={{ background: 'var(--ds-brand)' }}
              >
                <div className="flex items-center gap-2">
                  <Trash2 className="w-4 h-4 text-white/80" />
                  <span className="text-white text-sm font-semibold">Delete Post</span>
                </div>
                <button
                  type="button"
                  onClick={() => setConfirmDelete(null)}
                  className="w-6 h-6 flex items-center justify-center rounded text-white/70 hover:text-white hover:bg-white/20 transition-colors touch-manipulation"
                  aria-label="Close"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>

              {/* Content */}
              <div className="p-4 space-y-4">
                <div className="space-y-1.5">
                  <p className="t-card-title">
                    Delete this post?
                  </p>
                  <p className="t-meta leading-relaxed">
                    <span className="t-strong font-medium">"{confirmDelete.title}"</span> will be permanently deleted. This cannot be undone.
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => void handleDeleteConfirmed()}
                    className="ds-btn ds-btn-danger flex-1 touch-manipulation"
                  >
                    <Trash2 className="w-3.5 h-3.5 shrink-0" />
                    Yes, delete
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirmDelete(null)}
                    className="ds-btn ds-btn-secondary flex-1 touch-manipulation"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>,
      document.body
    )}

    {/* ── Rotate API key modal (admin only) ───────────────────────────── */}
    {rotateState !== 'idle' && createPortal(
      <AnimatePresence>
        <motion.div
          key="rotate-backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
          style={{ background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }}
          onClick={(e) => { if (e.target === e.currentTarget) handleCloseRotateModal(); }}
        >
          <motion.div
            key="rotate-card"
            initial={{ opacity: 0, scale: 0.95, y: 12 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 12 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="ds-card-pop w-full max-w-md overflow-hidden"
          >
            {/* Title bar */}
            <div className="flex items-center justify-between px-5 py-4"
                 style={{ background: 'var(--ds-brand)' }}>
              <div className="flex items-center gap-2 text-white font-semibold">
                <Key className="w-4 h-4" />
                <span>Rotate API Key</span>
              </div>
              <button
                type="button"
                onClick={handleCloseRotateModal}
                className="p-1 rounded hover:bg-white/20 transition-colors text-white"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Body */}
            <div className="p-5 space-y-4">
              {/* Confirm step */}
              {rotateState === 'confirm' && (
                <>
                  <p className="t-body leading-relaxed">
                    This will generate a new 48-character API key and invalidate the current one.
                  </p>
                  <p className="t-meta leading-relaxed">
                    The raw key will be shown <strong className="t-strong">only once</strong> — make sure you copy it and update your Supabase secret and any CI/CD environment before closing.
                  </p>
                  <div className="flex gap-2 pt-1">
                    <button
                      type="button"
                      onClick={() => void handleRotateKey()}
                      className="ds-btn ds-btn-primary flex-1"
                    >
                      Generate new key
                    </button>
                    <button
                      type="button"
                      onClick={handleCloseRotateModal}
                      className="ds-btn ds-btn-secondary flex-1"
                    >
                      Cancel
                    </button>
                  </div>
                </>
              )}

              {/* Generating */}
              {rotateState === 'generating' && (
                <div className="flex items-center justify-center gap-3 py-6 t-meta">
                  <div className="w-5 h-5 border-2 border-t-transparent rounded-full animate-spin"
                       style={{ borderColor: 'var(--ds-brand)', borderTopColor: 'transparent' }} />
                  Rotating key…
                </div>
              )}

              {/* Error */}
              {rotateState === 'error' && (
                <>
                  <div className="ds-recessed-danger t-meta">
                    {rotateError || 'An unexpected error occurred. The key was NOT rotated.'}
                  </div>
                  <button
                    type="button"
                    onClick={handleCloseRotateModal}
                    className="ds-btn ds-btn-secondary w-full"
                  >
                    Close
                  </button>
                </>
              )}

              {/* Done — show key once */}
              {rotateState === 'done' && (
                <>
                  <div className="ds-recessed-ok t-meta">
                    Key rotated successfully (prefix: <code className="font-mono font-bold">{rotatePrefix}…</code>)
                  </div>

                  <div>
                    <p className="t-label mb-1.5">
                      New API key — copy now
                    </p>
                    <div className="flex gap-2">
                      <input
                        ref={keyRef}
                        readOnly
                        value={rotateKey}
                        onFocus={(e) => e.target.select()}
                        className="ds-input t-mono t-tag flex-1"
                      />
                      <button
                        type="button"
                        onClick={handleCopyKey}
                        title="Copy to clipboard"
                        className="ds-btn ds-btn-secondary"
                      >
                        {keyCopied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                        {keyCopied ? 'Copied' : 'Copy'}
                      </button>
                    </div>
                  </div>

                  <div className="ds-recessed-warn t-meta leading-relaxed space-y-1">
                    <p className="font-semibold">⚠ This key is shown only once and cannot be retrieved.</p>
                    <p>After closing this dialog, update the following:</p>
                    <ul className="list-disc list-inside space-y-0.5 mt-1">
                      <li>Supabase dashboard → Edge Functions → Secrets → <code className="font-mono">BLOG_PUBLISH_API_KEY</code></li>
                      <li>Netlify → Site settings → Environment variables → <code className="font-mono">VITE_BLOG_PUBLISH_API_KEY</code></li>
                      <li>Agency Postman / curl scripts</li>
                    </ul>
                  </div>

                  <button
                    type="button"
                    onClick={handleCloseRotateModal}
                    className="ds-btn ds-btn-secondary w-full"
                  >
                    I've saved the key — close
                  </button>
                </>
              )}
            </div>
          </motion.div>
        </motion.div>
      </AnimatePresence>,
      document.body
    )}
    </>
  );
};

// Lightweight Supabase-session guard: no admin panel password needed.
// Writers sign in via Google/LinkedIn (normal app login) and need user_role=editor or admin.
const BlogListPage: React.FC<{ embedded?: boolean }> = ({ embedded }) => {
  const { session, loading } = useAuthentication();
  const [refreshing, setRefreshing] = useState(false);

  const handleRefreshSession = async () => {
    setRefreshing(true);
    await supabase.auth.refreshSession();
    // Brief pause so the new JWT propagates to state
    setTimeout(() => window.location.reload(), 800);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="w-10 h-10 rounded-full border-2 border-t-transparent animate-spin"
             style={{ borderColor: 'var(--ds-brand)', borderTopColor: 'transparent' }} />
      </div>
    );
  }

  if (!session) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="ds-card text-center max-w-md">
          <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <FileText className="w-8 h-8 text-red-400" />
          </div>
          <h2 className="text-xl font-bold mb-2">Sign in required</h2>
          <p className="t-meta mb-6">
            Sign in to GoGrabJob with your Google or LinkedIn account to access the blog editor.
          </p>
          <a
            href="/login"
            className="ds-btn ds-btn-primary"
          >
            Go to sign in
          </a>
        </div>
      </div>
    );
  }

  // Debug: decode JWT to see raw claims
  let jwtClaims: Record<string, unknown> = {};
  try {
    const part = session.access_token.split('.')[1];
    jwtClaims = JSON.parse(atob(part.replace(/-/g, '+').replace(/_/g, '/'))) as Record<string, unknown>;
  } catch { /* ignore */ }

  const role = getJwtUserRole(session);
  if (!role) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="ds-card text-center max-w-md">
          <div className="w-16 h-16 bg-amber-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <FileText className="w-8 h-8 text-amber-400" />
          </div>
          <h2 className="text-xl font-bold mb-2">Access not granted</h2>
          <p className="t-meta mb-2">
            Your account does not have the <code className="ds-chip ds-chip-warn">editor</code> or <code className="ds-chip ds-chip-warn">admin</code> role.
          </p>

          {/* Debug panel */}
          <div className="mt-4 mb-5 ds-card-sm text-left t-mono overflow-auto max-h-52">
            <p className="t-label mb-1">Debug — session data</p>
            <p className="t-meta">user_id: <span className="t-ok">{session.user.id}</span></p>
            <p className="t-meta">app_metadata:
              <span className="t-warn"> {JSON.stringify(session.user.app_metadata)}</span>
            </p>
            <p className="t-meta">jwt.user_role:
              <span className="t-warn"> {String(jwtClaims.user_role ?? 'undefined')}</span>
            </p>
            <p className="t-meta">jwt.app_metadata:
              <span className="t-warn"> {JSON.stringify(jwtClaims.app_metadata ?? {})}</span>
            </p>
          </div>

          <p className="t-meta mb-6">
            Share the debug panel above with your administrator to verify which user_id needs the grant script,
            then click <strong className="t-strong">Refresh session</strong>.
          </p>
          <button
            type="button"
            onClick={handleRefreshSession}
            disabled={refreshing}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-amber-600 hover:bg-amber-500 disabled:opacity-60 text-white font-medium transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            {refreshing ? 'Refreshing…' : 'Refresh session'}
          </button>
        </div>
      </div>
    );
  }

  return <BlogListInner embedded={embedded} />;
};

export default BlogListPage;
